import { supabase } from './supabase';
import { fetchFundingRates as fetchFundingRatesAPI, fetchFearGreedIndex as fetchFearGreedAPI } from './coinglass';
import type { FundingRateSimple, FearGreedData } from '../types/coinglass';

const CACHE_DURATION_MS = 60_000; // 1 minute — match cron interval

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<any>>();

function fromCache<T>(key: string): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_DURATION_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data as T;
}

function toCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

// Primary: read from Supabase (cached by cron). Fallback: call CoinGlass API directly.
export async function fetchFundingRates(symbol: string = 'BTC'): Promise<FundingRateSimple[]> {
  const cacheKey = `funding_rates:${symbol}`;
  const cached = fromCache<FundingRateSimple[]>(cacheKey);
  if (cached) return cached;

  // Try Supabase first
  const { data, error } = await supabase
    .from('funding_rates')
    .select('*')
    .eq('symbol', symbol.toUpperCase())
    .order('created_at', { ascending: false })
    .limit(50);

  if (!error && data && data.length > 0) {
    const result: FundingRateSimple[] = data.map((row: any) => ({
      exchangeName: (row.exchange || 'Unknown').toUpperCase(),
      fundingRate: parseFloat(row.rate) || 0,
      nextFundingTime: row.next_funding_time || 0,
      predicatedRate: row.accumulated_rate_1d || 0,
      symbol: row.symbol,
    }));
    toCache(cacheKey, result);
    return result;
  }

  // Fallback: call CoinGlass API directly (bypasses proxy)
  console.warn('Supabase empty, falling back to CoinGlass API directly');
  const apiResult = await fetchFundingRatesAPI(symbol);
  toCache(cacheKey, apiResult);
  return apiResult;
}

export async function fetchFearGreedFromSupabase(): Promise<FearGreedData[]> {
  const cached = fromCache<FearGreedData[]>('fear_greed');
  if (cached) return cached;

  // Try Supabase first
  const { data, error } = await supabase
    .from('fear_greed_history')
    .select('*')
    .order('timestamp', { ascending: false })
    .limit(30);

  if (!error && data && data.length > 0) {
    const result: FearGreedData[] = data.map((row: any) => ({
      value: row.value,
      valueClassification: row.value_classification,
      timestamp: new Date(row.timestamp).getTime(),
    }));
    toCache('fear_greed', result);
    return result;
  }

  // Fallback: call CoinGlass API directly
  const apiResult = await fetchFearGreedAPI();
  toCache('fear_greed', apiResult);
  return apiResult;
}
